import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import { FaBars } from 'react-icons/fa';
import { CommandPaletteProvider } from '../common/CommandPalette';
import CrisisBanner from '../common/CrisisBanner';
import { CrisisProvider } from '../../contexts/CrisisContext';
import { recordVisit } from '../../config/teams';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  position: relative;
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  margin-left: ${({ $sidebarOpen }) => ($sidebarOpen ? '270px' : '0')};
  padding: 2rem;
  /* El botón flotante (SidebarToggle) es fixed y se superpone al contenido
     en ambos estados. Se reserva su ancho a la izquierda para que no tape
     el título de la página. */
  padding-left: ${({ $sidebarOpen }) => ($sidebarOpen ? '4rem' : '4.75rem')};
  background-color: var(--color-secondary);
  transition: margin-left 0.28s ease, padding-left 0.28s ease;

  @media (max-width: 1024px) {
    margin-left: ${({ $sidebarOpen }) => ($sidebarOpen ? '250px' : '0')};
    padding: 1.5rem;
    padding-left: ${({ $sidebarOpen }) => ($sidebarOpen ? '4rem' : '4.75rem')};
  }

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding: 1.25rem 1.25rem calc(4.5rem + env(safe-area-inset-bottom, 0px));
  }

  @media (max-width: 480px) {
    padding: 1rem 1rem calc(4.5rem + env(safe-area-inset-bottom, 0px));
  }
`;

// Botón flotante para abrir/cerrar el sidebar (desktop/tablet).
const SidebarToggle = styled.button`
  position: fixed;
  top: 20px;
  left: ${({ $sidebarOpen }) => ($sidebarOpen ? '286px' : '20px')};
  background-color: var(--color-primary);
  color: white;
  border: 1px solid var(--border-soft);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: var(--z-toggle);
  box-shadow: var(--shadow-sm);
  transition: left 0.28s ease, background-color 0.2s ease;

  &:hover { background-color: var(--color-tertiary); }

  @media (max-width: 1024px) {
    left: ${({ $sidebarOpen }) => ($sidebarOpen ? '266px' : '20px')};
  }

  /* En móvil, la navegación vive en la barra inferior. */
  @media (max-width: 768px) { display: none; }
`;

// Backdrop para cerrar el sidebar en móvil.
const SidebarOverlay = styled.div`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: calc(var(--z-sidebar) - 1);

  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  }
`;

// Registra la visita a cada sección para alimentar "Frecuentes"/"Recientes".
const VisitTracker = () => {
  const location = useLocation();
  useEffect(() => { recordVisit(location.pathname); }, [location.pathname]);
  return null;
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => setSidebarOpen((v) => !v);

  return (
    <CrisisProvider>
    <CommandPaletteProvider>
      <VisitTracker />
      <LayoutContainer>
        <SidebarToggle $sidebarOpen={sidebarOpen} onClick={toggleSidebar} aria-label="Mostrar u ocultar menú">
          <FaBars />
        </SidebarToggle>

        <SidebarOverlay $isOpen={sidebarOpen} onClick={toggleSidebar} />
        <Sidebar $isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

        <MainContent $sidebarOpen={sidebarOpen}>
          {/* Cuando hay una crisis abierta, encabeza todas las pantallas. */}
          <CrisisBanner />
          <Outlet />
        </MainContent>

        <MobileNav toggleSidebar={toggleSidebar} />
      </LayoutContainer>
    </CommandPaletteProvider>
    </CrisisProvider>
  );
};

export default Layout;
